import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../api";

const AuthContext = createContext(null);
const LOCAL_AUTH_STORAGE_KEY = "advahini:local-auth-token";

const clearLegacySession = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem("token");
  window.localStorage.removeItem("advahini:auth-session");
};

const getLocalAuthToken = () => {
  if (typeof window === "undefined") return "";
  return String(window.localStorage.getItem(LOCAL_AUTH_STORAGE_KEY) || "").trim();
};

const setLocalAuthToken = (token) => {
  if (typeof window === "undefined") return;
  const value = String(token || "").trim();
  if (value) {
    window.localStorage.setItem(LOCAL_AUTH_STORAGE_KEY, value);
  } else {
    window.localStorage.removeItem(LOCAL_AUTH_STORAGE_KEY);
  }
};

const isAuthFailure = (error) => {
  const status = Number(error?.response?.status || 0);
  return status === 401 || status === 403;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    clearLegacySession();

    const loadSession = async () => {
      try {
        const token = getLocalAuthToken();
        if (!token) {
          api.setAuthToken(null);
          if (active) setUser(null);
          return;
        }
        api.setAuthToken(token);
        const response = await api.getCurrentUser();
        if (active) setUser(response.data);
      } catch (error) {
        console.error("Failed to load auth session", error);
        if (active) {
          setLocalAuthToken("");
          api.setAuthToken(null);
          setUser(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadSession();

    return () => {
      active = false;
    };
  }, []);

  const refreshUser = async () => {
    const token = getLocalAuthToken();
    if (!token) {
      api.setAuthToken(null);
      setUser(null);
      return null;
    }
    api.setAuthToken(token);
    try {
      const response = await api.getCurrentUser();
      setUser(response.data);
      return response.data;
    } catch (error) {
      if (isAuthFailure(error)) {
        setLocalAuthToken("");
        api.setAuthToken(null);
        setUser(null);
        return null;
      }
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.loginLocal(String(email || "").trim().toLowerCase(), password);
      const token = response?.data?.access_token || "";
      const nextUser = response?.data?.user || null;
      setLocalAuthToken(token);
      api.setAuthToken(token || null);
      setUser(nextUser);
      return { success: true, user: nextUser };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        message: error.message || error.response?.data?.detail || "Login failed",
      };
    }
  };

  const register = async (email, password, fullName, companyName) => {
    try {
      const response = await api.registerLocal({
        email: String(email || "").trim().toLowerCase(),
        password,
        fullName,
        companyName,
      });
      const token = response?.data?.access_token || "";
      const nextUser = response?.data?.user || null;
      setLocalAuthToken(token);
      api.setAuthToken(token || null);
      setUser(nextUser);
      return {
        success: true,
        user: nextUser,
        message: response?.data?.message || "Account created successfully.",
      };
    } catch (error) {
      console.error("Registration error:", error);
      return {
        success: false,
        message: error.message || error.response?.data?.detail || "Registration failed",
      };
    }
  };

  const loginWithGoogle = async (credential, companyName = "") => {
    try {
      const response = await api.googleAuthLocal({ credential, companyName });
      const token = response?.data?.access_token || "";
      const nextUser = response?.data?.user || null;
      setLocalAuthToken(token);
      api.setAuthToken(token || null);
      setUser(nextUser);
      return { success: true, user: nextUser };
    } catch (error) {
      console.error("Google auth error:", error);
      return {
        success: false,
        message: error.message || error.response?.data?.detail || "Google sign-in failed",
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      void email;
      return {
        success: true,
        message: "Password reset email is currently handled by the backend.",
      };
    } catch (error) {
      console.error("Forgot password error:", error);
      return {
        success: false,
        message: error.message || "Unable to send reset email",
      };
    }
  };

  const completePasswordReset = async (_token, password) => {
    try {
      void password;
      return {
        success: false,
        message: "Password reset links are currently handled by the backend.",
      };
    } catch (error) {
      console.error("Reset password error:", error);
      return {
        success: false,
        message: error.message || "Unable to reset password",
      };
    }
  };

  const completeEmailVerification = async () => ({
    success: true,
    message: "Email verification is currently handled by the backend.",
  });

  const resendVerificationEmail = async (email) => {
    try {
      void email;
      return {
        success: true,
        message: "Email verification is currently handled by the backend.",
      };
    } catch (error) {
      console.error("Resend verification error:", error);
      return {
        success: false,
        message: error.message || "Unable to resend verification email",
      };
    }
  };

  const logout = async () => {
    setLocalAuthToken("");
    clearLegacySession();
    api.setAuthToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        loginWithGoogle,
        forgotPassword,
        completePasswordReset,
        completeEmailVerification,
        resendVerificationEmail,
        refreshUser,
        setCurrentUser: setUser,
        logout,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
